/**
 * Consolidated User Tracking Context
 * Single context provider that handles all tracking functionality
 */

"use client";

import React, { createContext, useContext, ReactNode } from 'react';
import { useUserTracking } from '../hooks/useUserTracking';

// ============================================================================
// CONTEXT
// ============================================================================

interface UserTrackingContextType {
  // Current data
  currentPage: any;
  currentPageTime: number;
  userActivity: any;
  trackingData: any;
  interactions: any[];
  
  // Session data
  session: any;
  isLoggedIn: boolean;
  sessionValid: boolean;
  
  // Actions
  trackPageEntry: (url?: string, title?: string) => void;
  trackUserAction: (actionType: string, element?: HTMLElement, coordinates?: { x: number; y: number }, metadata?: Record<string, any>) => void;
  forceUpdate: () => void;
  forcePageTracking: () => void;
  formatTimeAsObject: (milliseconds: number) => { m: number; s: number };
  
  // Loading states
  isLoading: boolean;
  error: string | null;
}

const UserTrackingContext = createContext<UserTrackingContextType | null>(null);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface UserTrackingProviderProps {
  children: ReactNode;
}

export function UserTrackingProvider({ children }: UserTrackingProviderProps) {
  const trackingData = useUserTracking();

  // Log tracking data for debugging
  // useEffect(() => {
  //   if (process.env.NODE_ENV === 'development') {
  //     console.log('🎯 User Tracking Data:', trackingData.trackingData);
  //   }
  // }, [trackingData.trackingData]);

  return (
    <UserTrackingContext.Provider value={trackingData}>
      {children}
    </UserTrackingContext.Provider>
  );
}

// ============================================================================
// HOOK TO USE CONTEXT
// ============================================================================

export function useUserTrackingContext(): UserTrackingContextType {
  const context = useContext(UserTrackingContext);
  
  if (!context) {
    throw new Error('useUserTrackingContext must be used within a UserTrackingProvider');
  }
  
  return context;
}

// ============================================================================
// SIMPLIFIED HOOKS
// ============================================================================

/**
 * Hook to get current page data
 */
export function useCurrentPage() {
  const { currentPage, trackPageEntry } = useUserTrackingContext();
  return { currentPage, trackPageEntry };
}

/**
 * Hook to get user activity data
 */
export function useUserActivity() {
  const { userActivity, interactions, trackUserAction } = useUserTrackingContext();
  return { userActivity, interactions, trackUserAction };
}

/**
 * Hook to get session data
 */
export function useSession() {
  const { session, isLoggedIn, sessionValid } = useUserTrackingContext();
  return { session, isLoggedIn, sessionValid };
}

/**
 * Hook to get all tracking data
 */
export function useTrackingData() {
  const { trackingData, forceUpdate } = useUserTrackingContext();
  return { trackingData, forceUpdate };
}
